import { aiJson } from "@/services/ai.service";
import type { TopicSources } from "@/services/source.service";

/**
 * Bewertung eines gehaltenen Vortrags.
 *
 * Zwei getrennte Noten, weil es zwei verschiedene Fähigkeiten sind:
 *   Inhalt   – was wurde gesagt, was fehlt, was war falsch (gegen das Material)
 *   Methodik – wie wurde es gesagt: Fachbegriffe, Struktur, Adressatenbezug,
 *              Füllwörter, Redetempo
 *
 * Bewertet wird ausschließlich gegen das eigene Material des Nutzers. Ein
 * Punktabzug für etwas, das in seinen Notizen gar nicht steht, wäre unfair
 * und würde das Ergebnis wertlos machen.
 */

export type Mode = "recall" | "feynman" | "gespraech";

export interface Criterion {
  name: string;
  mark: "++" | "+" | "o" | "-" | "--";
  comment: string;
}

export interface SpeechRating {
  contentPct: number;
  methodPct: number;
  summary: string;
  criteria: Criterion[];
  covered: string[];
  missing: string[];
  /** Fachbegriffe, die falsch oder gar nicht verwendet wurden. */
  terms: { term: string; note: string }[];
  /** Genau ein Auftrag für die nächste Runde. */
  nextFocus: string;
}

const AUDIENCES: Record<string, string> = {
  grundschule: "einem Grundschulkind (8 Jahre): keine Fachbegriffe ohne Bild oder Vergleich",
  mitschueler: "Mitschülerinnen und Mitschülern der eigenen Klasse: Fachbegriffe erlaubt, aber erklärt",
  fach: "einem Fachpublikum: präzise Fachsprache, keine Vereinfachung",
  pruefung: "einer Prüfungskommission: strukturiert, belegt, mit Fazit",
};

export function audienceLabel(key: string): string {
  return (
    { grundschule: "Grundschulkind", mitschueler: "Mitschüler", fach: "Fachpublikum", pruefung: "Prüfungskommission" }[key] ??
    "Zuhörer"
  );
}

export const AUDIENCE_KEYS = Object.keys(AUDIENCES);

export interface GradeInput {
  mode: Mode;
  audience: string;
  round: number;
  seconds: number;
  limit: number;
  transcript: string;
  src: TopicSources;
  topicTitle: string;
  /** Was in der letzten Runde als Schwerpunkt gesetzt wurde. */
  lastFocus?: string;
}

export async function gradeSpeech(input: GradeInput): Promise<SpeechRating> {
  const words = input.transcript.split(/\s+/).filter(Boolean).length;
  const wpm = input.seconds > 0 ? Math.round((words / input.seconds) * 60) : 0;

  const task =
    input.mode === "recall"
      ? "Der Schüler hat FREI ALLES wiedergegeben, was er zum Thema weiß – ohne Material vor sich " +
        "(Active Recall). Bewerte, wie vollständig und richtig der Abruf gegenüber dem Material war."
      : `Der Schüler hat das Thema ${AUDIENCES[input.audience] ?? "seinen Zuhörern"} erklärt ` +
        "(Feynman-Methode). Bewerte zusätzlich streng, ob die Sprache wirklich zu dieser Zielgruppe passt.";

  const focus = input.lastFocus
    ? `\nIn der letzten Runde lautete der Auftrag: „${input.lastFocus}". Sag im summary ausdrücklich, ob das diesmal besser war.`
    : "";

  return aiJson<SpeechRating>(
    "Du bist eine erfahrene Lehrkraft und bewertest einen mündlichen Vortrag. " +
      `${task}\n\n` +
      "ABSOLUTE REGEL: Maßstab ist AUSSCHLIESSLICH das mitgelieferte MATERIAL des Schülers. " +
      "Was dort nicht steht, darf nicht als Lücke gewertet werden – auch wenn du es fachlich kennst.\n" +
      "Das Transkript stammt aus einer automatischen Spracherkennung: Rechtschreibung, Komma und " +
      "kleine Verhörer sind KEIN Fehler. Bewerte, was offensichtlich gemeint war.\n\n" +
      "Zwei getrennte Noten:\n" +
      "- contentPct (0–100): Vollständigkeit und Richtigkeit gegenüber dem Material.\n" +
      "- methodPct (0–100): Vortragsweise – roter Faden, saubere Verwendung der Fachbegriffe, " +
      "Adressatenbezug, Beispiele, Füllwörter, Redetempo.\n\n" +
      "Gib NUR JSON zurück:\n" +
      '{"contentPct":0-100,"methodPct":0-100,' +
      '"summary":"3-4 Sätze, persönlich und ermutigend, wie mündliches Feedback nach einem Referat",' +
      '"criteria":[{"name":"Fachliche Richtigkeit|Vollständigkeit|Roter Faden|Fachsprache|Adressatenbezug|Beispiele|Redefluss",' +
      '"mark":"++|+|o|-|--","comment":"ein Satz"}],' +
      '"covered":["Punkte aus dem Material, die er getroffen hat", …max 6],' +
      '"missing":["wichtige Punkte aus dem Material, die fehlten, wichtigste zuerst", …max 6],' +
      '"terms":[{"term":"Fachbegriff","note":"falsch verwendet / gar nicht genannt / gut erklärt"}, …max 6],' +
      '"nextFocus":"EIN konkreter Auftrag für die nächste Runde, ein Satz"}\n' +
      "Erzeuge 5–7 criteria. Deutsch." +
      focus,
    `THEMA: ${input.topicTitle}\n` +
      `RUNDE: ${input.round}\n` +
      `REDEZEIT: ${input.seconds} s von erlaubten ${input.limit} s, ${words} Wörter (${wpm} Wörter/Minute)\n\n` +
      `MATERIAL DES SCHÜLERS:\n${input.src.text.slice(0, 30000)}\n\n` +
      `TRANSKRIPT DES VORTRAGS:\n${input.transcript || "(nichts gesagt)"}`,
    2500,
    "medium"
  ).then(normalize);
}

function normalize(r: SpeechRating): SpeechRating {
  const pct = (v: unknown) => Math.max(0, Math.min(100, Math.round(Number(v) || 0)));
  const marks = new Set(["++", "+", "o", "-", "--"]);
  return {
    contentPct: pct(r.contentPct),
    methodPct: pct(r.methodPct),
    summary: String(r.summary ?? ""),
    criteria: (Array.isArray(r.criteria) ? r.criteria : [])
      .map((c) => ({
        name: String(c?.name ?? "").trim(),
        mark: (marks.has(String(c?.mark)) ? String(c.mark) : "o") as Criterion["mark"],
        comment: String(c?.comment ?? "").trim(),
      }))
      .filter((c) => c.name)
      .slice(0, 8),
    covered: strings(r.covered),
    missing: strings(r.missing),
    terms: (Array.isArray(r.terms) ? r.terms : [])
      .map((t) => ({ term: String(t?.term ?? "").trim(), note: String(t?.note ?? "").trim() }))
      .filter((t) => t.term)
      .slice(0, 8),
    nextFocus: String(r.nextFocus ?? "").trim(),
  };
}

function strings(v: unknown): string[] {
  return Array.isArray(v) ? v.map((x) => String(x).trim()).filter(Boolean).slice(0, 6) : [];
}
